import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { ShopService } from '@app/services/shops/shop.service';
import {
  CheckoutExtensionCommonConfig,
  CheckoutUpsellExtension,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { IUpsellThresholdTypes } from '@pdqdev/pdq-shared/dist/interfaces/IThresholdTypes';
import { hasActiveGroupDiscounts, isAutonomousGroup, MAX_ALLOWED_DISCOUNT_VALUES } from '../../autonomous-discount';
import { AUTONOMOUS_DISCOUNT_PITCH } from '../../../../shared/ai-mark/beta-mark-copy';

interface ThresholdTypeOption {
  text: string;
  value: IUpsellThresholdTypes;
  defaultChecked: boolean;
  checked: (val: string) => boolean;
}

@Component({
  selector: 'checkout-upsell-settings',
  templateUrl: './checkout-upsell-settings.component.html',
  styleUrls: ['./checkout-upsell-settings.component.scss'],
  standalone: false,
})
export class CheckoutUpsellSettingsComponent {
  @Input() formGroup: UntypedFormGroup;
  @Input() defaultCurrencySymbol: string;
  @Input() showDiscountSection = false;
  @Input() showLegacyDiscountControls = false;
  @Input() showStaticDiscountControls = true;
  @Input() autonomousDiscountFlagOn = false;
  @Input() allowedDiscountValues: UntypedFormArray;

  @Output() autonomousDiscountToggled = new EventEmitter<boolean>();
  @Output() addAllowedDiscountValue = new EventEmitter<void>();
  @Output() removeAllowedDiscountValue = new EventEmitter<number>();

  readonly autonomousDiscountPitch = AUTONOMOUS_DISCOUNT_PITCH;

  get isAutonomousDiscount(): boolean {
    return isAutonomousGroup(this.formGroup?.get('discountConfiguration'));
  }

  get showAutonomousReadOnlyNotice(): boolean {
    return this.isAutonomousDiscount && !this.autonomousDiscountFlagOn;
  }

  get hasGroupDiscounts(): boolean {
    return hasActiveGroupDiscounts((this.formGroup?.get('productSelection') as UntypedFormArray)?.value ?? []);
  }

  get isAddAllowedValueDisabled(): boolean {
    return (this.allowedDiscountValues?.length ?? 0) >= MAX_ALLOWED_DISCOUNT_VALUES;
  }

  get allowedDiscountValuesError(): string {
    return this.formGroup?.get('discountConfiguration')?.errors?.allowedDiscountValues ?? '';
  }

  PlacementMode = CheckoutExtensionCommonConfig.PlacementMode;
  DiscountType = CheckoutUpsellExtension.DiscountType;

  discountTypeOptions = [
    { label: 'Fixed amount', value: CheckoutUpsellExtension.DiscountType.FixedAmount },
    { label: 'Percentage', value: CheckoutUpsellExtension.DiscountType.Percentage },
  ];

  labelColorOptions = [
    { label: 'Default', value: 'default' },
    { label: 'Base', value: 'base' },
    { label: 'Info', value: 'info' },
    { label: 'Critical', value: 'critical' },
    { label: 'Success', value: 'success' },
    { label: 'Warning', value: 'warning' },
    { label: 'Accent', value: 'accent' },
  ];

  readonly IUpsellThresholdTypes = IUpsellThresholdTypes;

  thresholdTypeOptions: ThresholdTypeOption[] = [
    {
      text: 'Cart Value',
      value: IUpsellThresholdTypes.CartValue,
      defaultChecked: true,
      checked: (val: string) => val === IUpsellThresholdTypes.CartValue,
    },
    {
      text: 'Item Count',
      value: IUpsellThresholdTypes.ItemCount,
      defaultChecked: false,
      checked: (val: string) => val === IUpsellThresholdTypes.ItemCount,
    },
    {
      text: 'No Threshold',
      value: IUpsellThresholdTypes.NoThreshold,
      defaultChecked: false,
      checked: (val: string) => val === IUpsellThresholdTypes.NoThreshold,
    },
  ];

  constructor(public shopService: ShopService) {}
}
